import { useRef, useEffect, useState } from 'react';
import { useFootprint } from '../hooks/useFootprint';
import { Hash, Link, CheckCircle2, Coins, RefreshCw } from 'lucide-react';

export function Footprint({ defaultShowChain = false }: { defaultShowChain?: boolean }) {
  const { deeds, hablCoins, transactions, getIntensity, syncWithCloud, isSyncing } = useFootprint();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showChain, setShowChain] = useState(defaultShowChain);

  // Scroll to TODAY on mount
  useEffect(() => {
    if (scrollRef.current) {
        // Calculate approx position of "Today"
        // 9 months past = approx 39 weeks.
        // Each week col is approx 14px wide.
        // 39 * 14 = 546px.
        // Let's just scroll to near the end but leave some space.
        // Or better, scroll to max to ensure we see the latest.
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth - 100; // Show a bit of future/padding
    }
  }, []);

  // Generate Year View: Last 9 Months -> Today -> Next 3 Months
  const today = new Date();
  
  // Start date: 9 months ago
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - 9);
  
  // Align start date to the previous SUNDAY (Standard Calendar View)
  const dayOfWeek = startDate.getDay(); // 0 (Sun) to 6 (Sat)
  // If we want to start on Sunday, we subtract 'dayOfWeek' days
  startDate.setDate(startDate.getDate() - dayOfWeek);

  // Generate days for 52 weeks (approx 1 year total)
  // This will naturally cover ~9 months past and ~3 months future
  const days: string[] = [];
  const iterDate = new Date(startDate);
  
  for (let i = 0; i < 371; i++) { // 53 weeks * 7 days
      days.push(iterDate.toLocaleDateString('en-CA'));
      iterDate.setDate(iterDate.getDate() + 1);
  }

  // Calculate total deeds
  const totalDeeds = Object.values(deeds).reduce((a, b) => a + b, 0);
  const activeDays = Object.keys(deeds).length;

  // Group days by week (Column-major layout)
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Get month labels
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthLabels: { label: string, index: number }[] = [];
  
  weeks.forEach((week, index) => {
    const firstDay = new Date(week[0]);
    const month = firstDay.getMonth();
    
    // Check if this week starts a new month OR is the first week
    if (index === 0) {
        monthLabels.push({ label: months[month], index });
    } else {
        const prevWeekFirstDay = new Date(weeks[index - 1][0]);
        if (prevWeekFirstDay.getMonth() !== month) {
            monthLabels.push({ label: months[month], index });
        }
    }
  });

  return (
    <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 text-white w-full max-w-md mx-auto mt-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            Active Days <span className="text-slate-500 font-normal text-xs ml-1">ⓘ</span> 
            <button 
                onClick={() => setShowChain(!showChain)}
                className={`p-1 rounded-md transition-colors ${showChain ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-600 hover:text-emerald-500'}`}
                title="Verify on Habl Chain"
            >
                <Link className="w-4 h-4" />
            </button>
            <button 
                onClick={() => syncWithCloud?.()}
                className={`p-1 rounded-md transition-colors text-slate-600 hover:text-emerald-500 ${isSyncing ? 'animate-spin text-emerald-500' : ''}`}
                title="Sync with Cloud"
            >
                <RefreshCw className="w-4 h-4" />
            </button>
          </h3>
          <p className="text-xs text-slate-400">Consistency is beloved to Allah.</p>
        </div>
        <div className="text-right">
           <div className="flex items-center justify-end gap-1 text-amber-400">
               <Coins className="w-4 h-4" />
               <span className="text-xl font-bold">{hablCoins}</span>
           </div>
           <span className="text-[10px] text-slate-500 uppercase tracking-wider">Habl Coins</span>
        </div>
      </div>

      {showChain ? (
        <div className="space-y-3 mb-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono mb-2 border-b border-slate-800 pb-2">
                <Hash className="w-3 h-3" /> Immutable Ledger (Last 5)
            </div>
            {transactions.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-xs italic">No blocks mined yet. Start doing good deeds!</div>
            ) : (
                transactions.slice(0, 5).map((tx, idx) => (
                    <div key={tx.id} className="bg-slate-950 p-3 rounded-lg border border-slate-800 font-mono text-[10px] relative overflow-hidden group">
                        <div className="flex justify-between text-slate-400 mb-1">
                            <span>Block #{transactions.length - idx}</span>
                            <span>{new Date(tx.timestamp).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between items-center text-emerald-500/80">
                            <span className="truncate max-w-[150px]">{tx.hash}</span>
                            <div className="flex items-center gap-1 text-white font-bold bg-slate-800 px-1.5 py-0.5 rounded">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                {tx.amount} {tx.type === 'deed' ? 'Coin' : 'Coins'}
                            </div>
                        </div>
                        <div className="text-[9px] text-slate-600 mt-1 truncate">{tx.description}</div>
                        <div className="absolute left-0 top-0 w-1 h-full bg-emerald-500/20 group-hover:bg-emerald-500 transition-colors"></div>
                    </div>
                ))
            )}
            <button 
                onClick={() => setShowChain(false)}
                className="w-full text-center text-[10px] text-slate-500 hover:text-white pt-2"
            >
                Close Ledger
            </button>
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <div className="flex gap-4 mb-6 text-xs">
            <div className="bg-slate-800 px-3 py-2 rounded-lg border border-slate-700">
               <span className="block text-emerald-400 font-bold">{activeDays}</span>
               <span className="text-slate-500">Active Days</span>
            </div>
            <div className="bg-slate-800 px-3 py-2 rounded-lg border border-slate-700">
               <span className="block text-emerald-400 font-bold">
                 {activeDays > 0 ? Math.round(totalDeeds / activeDays) : 0}
               </span>
               <span className="text-slate-500">Avg / Day</span>
            </div>
          </div>

          {/* GitHub-style Heatmap Container */}
          <div 
            ref={scrollRef}
            className="w-full overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900"
          >
            {/* Inner container with min-width to ensure scrolling */}
            <div className="min-w-max relative pl-8"> 
                {/* Month Labels - Positioned Absolutely relative to the grid start */}
                <div className="h-6 relative mb-1 w-full whitespace-nowrap">
                    {monthLabels.map((m: any) => (
                        <span 
                            key={m.label + m.index} 
                            style={{ left: `${(m.index * 14) + 32}px` }} 
                            className="absolute text-[10px] text-slate-500"
                        >
                            {m.label}
                        </span>
                    ))}
                </div>

                <div className="flex">
                    {/* Day Labels (Mon/Wed/Fri) - Sticky Left */}
                    <div className="flex flex-col gap-1 text-[9px] text-slate-600 mr-2 sticky left-0 bg-slate-900 z-10 pt-[2px]">
                        <span className="h-2.5 flex items-center"></span>
                        <span className="h-2.5 flex items-center">Mon</span>
                        <span className="h-2.5 flex items-center"></span>
                        <span className="h-2.5 flex items-center">Wed</span>
                        <span className="h-2.5 flex items-center"></span>
                        <span className="h-2.5 flex items-center">Fri</span>
                        <span className="h-2.5 flex items-center"></span>
                    </div>

                    {/* Grid */}
                    <div className="flex gap-1">
                        {weeks.map((week, wIndex) => (
                            <div key={wIndex} className="flex flex-col gap-1">
                                {week.map((date) => {
                                    const count = deeds[date] || 0;
                                    const isToday = date === new Date().toISOString().split('T')[0];
                                    return (
                                        <div 
                                        key={date}
                                        title={`${date}: ${count} deeds`}
                                        className={`w-2.5 h-2.5 rounded-sm transition-all duration-300 ${getIntensity(count)} ${isToday ? 'ring-1 ring-white ring-offset-1 ring-offset-slate-900' : ''}`} 
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center mt-4 text-[10px] text-slate-600">
            <span>Less</span>
            <div className="flex gap-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-slate-800" title="0 Deeds"></div>
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-900" title="1 Deed (Login)"></div>
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-600" title="2 Deeds"></div>
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]" title="3+ Deeds (Bright)"></div>
            </div>
            <span>More</span>
          </div>
        </>
      )}
    </div>
  );
}
